/**
 * Form Auto-Save System
 * WCAG 2.2.6 Compliant - Prevents data loss on session timeout
 *
 * Automatically saves form data to localStorage and restores it on page load
 */

(function () {
  "use strict";

  // Configuration
  const CONFIG = {
    // Auto-save interval in milliseconds
    SAVE_INTERVAL: 30000, // 30 seconds

    // Storage key prefix
    STORAGE_PREFIX: "wcag_form_autosave_",

    // Forms to exclude from auto-save (by ID or class)
    EXCLUDE_FORMS: [
      "login",
      "password",
      "payment",
      "credit-card",
      "storefront_password",
      "newsletter", // 排除newsletter
      "footer-newsletter", // 排除footer中的newsletter
      "subscribe", // 排除subscribe
    ],

    // Fields to exclude from auto-save (sensitive data)
    EXCLUDE_FIELDS: [
      "password",
      "credit_card",
      "card_number",
      "cvv",
      "security_code",
      "ssn",
    ],

    // Maximum storage age in milliseconds (7 days)
    MAX_STORAGE_AGE: 7 * 24 * 60 * 60 * 1000,
  };

  let saveTimers = new Map();
  let trackedForms = new Set();

  // --- Added: resilient localStorage helpers (Quota / invalid data handling) ---
  function isQuotaExceededError(e) {
    return (
      e &&
      (e.name === "QuotaExceededError" ||
        e.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
        e.code === 22 ||
        e.code === 1014)
    );
  }

  function getAllAutoSaveKeys() {
    try {
      return Object.keys(localStorage).filter((k) =>
        k.startsWith(CONFIG.STORAGE_PREFIX)
      );
    } catch (_) {
      return [];
    }
  }

  function evictOldestAutoSaveEntry() {
    const keys = getAllAutoSaveKeys();
    let oldestKey = null;
    let oldestTs = Infinity;

    for (const key of keys) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) {
          continue;
        }
        const parsed = JSON.parse(raw);
        const ts = Number(parsed?.timestamp);
        if (!Number.isFinite(ts)) {
          // Corrupt/unknown entry: prefer deleting it
          localStorage.removeItem(key);
          continue;
        }
        if (ts < oldestTs) {
          oldestTs = ts;
          oldestKey = key;
        }
      } catch (_) {
        // Corrupt entry: delete it
        try {
          localStorage.removeItem(key);
        } catch (_) {}
      }
    }

    if (oldestKey) {
      try {
        localStorage.removeItem(oldestKey);
        return true;
      } catch (_) {
        return false;
      }
    }

    return false;
  }

  function safeSetLocalStorageItem(key, value) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      // Most likely: normal-mode localStorage quota already full
      if (isQuotaExceededError(e)) {
        try {
          // 1) remove expired entries first
          cleanupOldData();

          // 2) if still full, evict the oldest autosave entry
          evictOldestAutoSaveEntry();

          // 3) retry once
          localStorage.setItem(key, value);
          return true;
        } catch (e2) {
          console.warn(
            "[WCAG Form Auto-Save] localStorage quota exceeded; could not persist autosave data after eviction.",
            e2
          );
          return false;
        }
      }

      console.warn("[WCAG Form Auto-Save] Failed to write localStorage:", e);
      return false;
    }
  }
  // --- End added helpers ---

  /**
   * Initialize the form auto-save system
   */
  function init() {
    // Find and track all forms on the page
    trackForms();

    // Clean up old saved data
    cleanupOldData();

    // Setup mutation observer for dynamically added forms
    setupMutationObserver();

    console.log("✓ WCAG Form Auto-Save System initialized");
  }

  /**
   * Track all forms on the page
   */
  function trackForms() {
    const forms = document.querySelectorAll("form");

    forms.forEach((form) => {
      if (shouldTrackForm(form)) {
        trackForm(form);
      }
    });
  }

  /**
   * Check if a form should be tracked
   */
  function shouldTrackForm(form) {
    // Skip if already tracked
    if (trackedForms.has(form)) {
      return false;
    }

    // Skip checkout forms (handled by Shopify)
    if (window.location.pathname.includes("/checkouts/")) {
      return false;
    }

    // Skip excluded forms
    const formId = form.id?.toLowerCase() || "";
    const formClass = form.className?.toLowerCase() || "";
    const formAction = form.action?.toLowerCase() || "";

    for (const exclude of CONFIG.EXCLUDE_FORMS) {
      if (
        formId.includes(exclude) ||
        formClass.includes(exclude) ||
        formAction.includes(exclude)
      ) {
        return false;
      }
    }

    // Only track forms with text inputs, textareas, or selects
    const relevantFields = form.querySelectorAll(
      'input[type="text"], input[type="email"], input[type="tel"], input[type="url"], textarea, select'
    );

    if (relevantFields.length === 0) {
      return false;
    }

    // Skip simple newsletter forms (only 1 email field)
    const emailFields = form.querySelectorAll('input[type="email"]');
    if (relevantFields.length === 1 && emailFields.length === 1) {
      console.log("Skipping simple newsletter form:", formId || formClass);
      return false;
    }

    return true;
  }

  /**
   * Track a specific form
   */
  function trackForm(form) {
    trackedForms.add(form);

    const formKey = getFormKey(form);

    // Restore saved data
    restoreFormData(form, formKey);

    // Add input event listeners
    const fields = getTrackableFields(form);
    fields.forEach((field) => {
      field.addEventListener("input", () => {
        scheduleFormSave(form, formKey);
      });

      field.addEventListener("change", () => {
        scheduleFormSave(form, formKey);
      });
    });

    // Clear saved data on successful form submission
    form.addEventListener("submit", () => {
      clearFormData(formKey);
    });

    // Show auto-save indicator
    addAutoSaveIndicator(form);
  }

  /**
   * Get all trackable fields in a form
   */
  function getTrackableFields(form) {
    const fields = form.querySelectorAll(
      'input[type="text"], input[type="email"], input[type="tel"], input[type="url"], input[type="number"], textarea, select'
    );

    return Array.from(fields).filter((field) => {
      const fieldName = (field.name || field.id || "").toLowerCase();

      // Skip excluded fields
      for (const exclude of CONFIG.EXCLUDE_FIELDS) {
        if (fieldName.includes(exclude)) {
          return false;
        }
      }

      // Skip hidden fields
      if (field.type === "hidden") {
        return false;
      }

      return true;
    });
  }

  /**
   * Generate a unique key for a form
   */
  function getFormKey(form) {
    const formId = form.id || "";
    const formAction = form.action || "";
    const formPath = window.location.pathname;

    return `${formPath}_${formId}_${formAction}`.replace(/[^a-zA-Z0-9]/g, "_");
  }

  /**
   * Schedule form data save
   */
  function scheduleFormSave(form, formKey) {
    // Clear existing timer
    if (saveTimers.has(formKey)) {
      clearTimeout(saveTimers.get(formKey));
    }

    // Schedule new save (debounced)
    const timer = setTimeout(() => {
      saveFormData(form, formKey);
    }, 2000); // Save 2 seconds after last input

    saveTimers.set(formKey, timer);
  }

  /**
   * Save form data to localStorage
   */
  function saveFormData(form, formKey) {
    const fields = getTrackableFields(form);
    const formData = {};
    let hasData = false;

    fields.forEach((field) => {
      const fieldName = field.name || field.id;
      if (!fieldName) return;

      let value = "";

      if (field.type === "checkbox") {
        value = field.checked;
      } else if (field.type === "radio") {
        if (field.checked) {
          value = field.value;
        }
      } else {
        value = field.value;
      }

      if (value) {
        formData[fieldName] = value;
        hasData = true;
      }
    });

    if (hasData) {
      const saveData = {
        data: formData,
        timestamp: Date.now(),
        path: window.location.pathname,
      };

      const storageKey = CONFIG.STORAGE_PREFIX + formKey;
      const payload = JSON.stringify(saveData);

      const ok = safeSetLocalStorageItem(storageKey, payload);
      if (ok) {
        // Show save indicator
        showSaveIndicator(form, "saved");
      }
    }
  }

  /**
   * Restore form data from localStorage
   */
  function restoreFormData(form, formKey) {
    try {
      const savedDataStr = localStorage.getItem(
        CONFIG.STORAGE_PREFIX + formKey
      );
      if (!savedDataStr) return;

      const savedData = JSON.parse(savedDataStr);

      // Check if data is too old
      if (Date.now() - savedData.timestamp > CONFIG.MAX_STORAGE_AGE) {
        localStorage.removeItem(CONFIG.STORAGE_PREFIX + formKey);
        return;
      }

      // Check if path matches
      if (savedData.path !== window.location.pathname) {
        return;
      }

      // Restore field values
      const fields = getTrackableFields(form);
      let restoredCount = 0;

      fields.forEach((field) => {
        const fieldName = field.name || field.id;
        if (!fieldName || !savedData.data[fieldName]) return;

        const value = savedData.data[fieldName];

        if (field.type === "checkbox") {
          field.checked = value;
          restoredCount++;
        } else if (field.type === "radio") {
          if (field.value === value) {
            field.checked = true;
            restoredCount++;
          }
        } else if (!field.value) {
          // Only restore if field is empty
          field.value = value;
          restoredCount++;
        }
      });

      if (restoredCount > 0) {
        showRestoreNotification(form, restoredCount);
      }
    } catch (e) {
      console.warn("Failed to restore form data:", e);
    }
  }

  /**
   * Clear saved form data
   */
  function clearFormData(formKey) {
    try {
      localStorage.removeItem(CONFIG.STORAGE_PREFIX + formKey);
    } catch (e) {
      console.warn("Failed to clear form data:", e);
    }
  }

  /**
   * Add auto-save indicator to form
   */
  function addAutoSaveIndicator(form) {
    // Check if indicator already exists
    if (form.querySelector(".wcag-autosave-indicator")) {
      return;
    }

    const indicator = document.createElement("div");
    indicator.className = "wcag-autosave-indicator";
    indicator.setAttribute("role", "status");
    indicator.setAttribute("aria-live", "polite");
    indicator.innerHTML = `
      <svg class="wcag-autosave-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
        <polyline points="17 21 17 13 7 13 7 21"/>
        <polyline points="7 3 7 8 15 8"/>
      </svg>
      <span class="wcag-autosave-text">Your progress is automatically saved</span>
    `;

    // Insert at the beginning of the form
    form.insertBefore(indicator, form.firstChild);

    // Add styles inline for immediate visibility
    const style = document.createElement("style");
    style.textContent = `
      .wcag-autosave-indicator {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        background-color: #f0f9ff;
        border: 1px solid #bae6fd;
        border-radius: 6px;
        color: #0369a1;
        font-size: 0.875rem;
      }
      .wcag-autosave-icon {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }
      .wcag-autosave-indicator.saved {
        background-color: #f0fdf4;
        border-color: #bbf7d0;
        color: #15803d;
      }
      .wcag-autosave-indicator.saved .wcag-autosave-text::before {
        content: "✓ ";
      }
    `;

    if (!document.querySelector("#wcag-autosave-styles")) {
      style.id = "wcag-autosave-styles";
      document.head.appendChild(style);
    }
  }

  /**
   * Show save indicator
   */
  function showSaveIndicator(form, status) {
    const indicator = form.querySelector(".wcag-autosave-indicator");
    if (!indicator) return;

    indicator.classList.add(status);

    setTimeout(() => {
      indicator.classList.remove(status);
    }, 2000);
  }

  /**
   * Show restore notification
   */
  function showRestoreNotification(form, count) {
    const notification = document.createElement("div");
    notification.className = "wcag-restore-notification";
    notification.setAttribute("role", "alert");
    notification.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <polyline points="23 4 23 10 17 10"/>
        <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
      </svg>
      <span>Restored ${count} saved field${
      count > 1 ? "s" : ""
    } from your previous session.</span>
      <button type="button" class="wcag-dismiss-btn" aria-label="Dismiss notification">×</button>
    `;

    // Add inline styles
    const style = document.createElement("style");
    if (!document.querySelector("#wcag-restore-styles")) {
      style.id = "wcag-restore-styles";
      style.textContent = `
        .wcag-restore-notification {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          margin-bottom: 20px;
          background-color: #fef3c7;
          border: 1px solid #fbbf24;
          border-radius: 6px;
          color: #92400e;
          font-size: 0.875rem;
          animation: wcagSlideIn 0.3s ease-out;
        }
        .wcag-restore-notification svg {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
        }
        .wcag-restore-notification span {
          flex: 1;
        }
        .wcag-dismiss-btn {
          background: none;
          border: none;
          color: inherit;
          font-size: 1.5rem;
          line-height: 1;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
        }
        @keyframes wcagSlideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `;
      document.head.appendChild(style);
    }

    form.insertBefore(notification, form.firstChild);

    // Dismiss button
    notification
      .querySelector(".wcag-dismiss-btn")
      .addEventListener("click", () => {
        notification.remove();
      });

    // Auto-dismiss after 10 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 10000);
  }

  /**
   * Clean up old saved data
   */
  function cleanupOldData() {
    try {
      const keys = Object.keys(localStorage);
      const now = Date.now();

      keys.forEach((key) => {
        if (!key.startsWith(CONFIG.STORAGE_PREFIX)) return;

        try {
          const raw = localStorage.getItem(key);
          if (!raw) {
            localStorage.removeItem(key);
            return;
          }

          const data = JSON.parse(raw);
          const ts = Number(data?.timestamp);

          // Invalid payload => remove
          if (!Number.isFinite(ts)) {
            localStorage.removeItem(key);
            return;
          }

          if (now - ts > CONFIG.MAX_STORAGE_AGE) {
            localStorage.removeItem(key);
          }
        } catch (e) {
          // Invalid data, remove it
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn("Failed to cleanup old form data:", e);
    }
  }

  /**
   * Setup mutation observer for dynamic forms
   */
  function setupMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Element node
            if (node.tagName === "FORM" && shouldTrackForm(node)) {
              trackForm(node);
            }

            // Check for forms within added nodes
            const forms = node.querySelectorAll?.("form");
            forms?.forEach((form) => {
              if (shouldTrackForm(form)) {
                trackForm(form);
              }
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Expose API for external use
  window.WCAGFormAutoSave = {
    saveForm: (form) => {
      const formKey = getFormKey(form);
      saveFormData(form, formKey);
    },
    clearForm: (form) => {
      const formKey = getFormKey(form);
      clearFormData(formKey);
    },
    restoreForm: (form) => {
      const formKey = getFormKey(form);
      restoreFormData(form, formKey);
    },
  };
})();
